import { Zap } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

interface InstantBookBadgeProps {
  /** `sm` for car cards, `md` for the detail header. */
  size?: "sm" | "md";
  className?: string;
}

/** Matches the tooltip's `w-56` class (14rem at the default 16px root). */
const TOOLTIP_WIDTH = 224;
/** Gap between the badge and the tooltip (was `mt-1.5`). */
const TOOLTIP_GAP = 6;
/** Minimum distance the tooltip keeps from the viewport edges. */
const VIEWPORT_MARGIN = 8;

/**
 * Solid brand-green "Instant Book" pill (Plan C). Explains itself via a styled
 * popover that opens on hover (desktop) and tap/click (mobile) — the native
 * `title` tooltip it replaces did not work on touch devices. Colours use the
 * `brand`/`popover` design tokens (never raw green hex) so the brand-color
 * guard passes.
 *
 * The popover renders through a portal to `document.body` with viewport-fixed
 * positioning: car cards clip absolutely-positioned children via the card
 * root's `overflow-hidden` (needed for the rounded corners), which cut the
 * popover off. Portalling escapes every ancestor clipping context. When the
 * badge sits near the bottom of the viewport the popover flips above it;
 * scrolling or resizing closes it so it can never drift away from its anchor.
 */
export function InstantBookBadge({ size = "sm", className }: InstantBookBadgeProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLSpanElement>(null);

  const reposition = useCallback(() => {
    const anchor = wrapRef.current;
    const tip = tipRef.current;
    if (!anchor || !tip) return;
    const rect = anchor.getBoundingClientRect();
    const tipHeight = tip.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let left = Math.min(rect.left, viewportWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN);
    left = Math.max(VIEWPORT_MARGIN, left);

    let top = rect.bottom + TOOLTIP_GAP;
    const overflowsBottom = top + tipHeight > viewportHeight - VIEWPORT_MARGIN;
    const fitsAbove = rect.top - TOOLTIP_GAP - tipHeight >= VIEWPORT_MARGIN;
    if (overflowsBottom && fitsAbove) {
      top = rect.top - TOOLTIP_GAP - tipHeight;
    }
    setPos({ top, left });
  }, []);

  // Measure and place the tooltip before paint so it never flashes at 0,0.
  useLayoutEffect(() => {
    if (open) reposition();
    else setPos(null);
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    function onDocPointer(e: MouseEvent | TouchEvent) {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target)) return;
      if (tipRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onScrollOrResize() {
      // The anchor moves with the page but the tooltip is viewport-fixed —
      // close instead of chasing it.
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("touchstart", onDocPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("touchstart", onDocPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  const closeUnlessMovingTo = (related: EventTarget | null, other: HTMLElement | null) => {
    if (related instanceof Node && other?.contains(related)) return;
    setOpen(false);
  };

  const padding = size === "md" ? "px-2.5 py-1 text-xs" : "px-2 py-0.5 text-[11px]";
  const iconSize = size === "md" ? "h-3.5 w-3.5" : "h-3 w-3";

  return (
    <span
      ref={wrapRef}
      className={cn("relative inline-flex", className)}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={(e) => closeUnlessMovingTo(e.relatedTarget, tipRef.current)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-label={t("carCard.social_proof.instant_book_tooltip")}
        onClick={(e) => {
          // The pill can sit inside a card-wide link; stop it from navigating.
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-brand font-semibold text-brand-foreground shadow-sm transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
          padding,
        )}
      >
        <Zap className={cn("shrink-0 fill-current", iconSize)} />
        {t("carCard.social_proof.instant_book")}
      </button>
      {open &&
        createPortal(
          <span
            ref={tipRef}
            role="tooltip"
            style={{ top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden" }}
            onMouseLeave={(e) => closeUnlessMovingTo(e.relatedTarget, wrapRef.current)}
            onClick={(e) => {
              // Keep clicks on the tooltip from reaching anything underneath.
              e.preventDefault();
              e.stopPropagation();
            }}
            className="fixed z-50 w-56 rounded-lg border border-popover-border bg-popover p-2.5 text-xs font-normal normal-case leading-snug text-popover-foreground shadow-lg"
          >
            {t("carCard.social_proof.instant_book_tooltip")}
          </span>,
          document.body,
        )}
    </span>
  );
}
